import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { deleteRequest, fetchTextGet, getRequest, postRequest, downloadBlobGet } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../../assets/css/style.css';
import '../../../assets/css/iconsmenu.css';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { PanelMenu } from 'primereact/panelmenu';
import { Avatar } from 'primereact/avatar';
import { DateTime } from 'luxon';
import { MdSearch, MdSearchOff } from 'react-icons/md';
import {
  AddIcon, EditIcon, CopyIcon, DeleteIcon, PrintIcon, SearchIcon, ExportIcon, VerticalLineIcon,
  BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, EditRowIcon,
  RefreshIcon,
} from '../../svg/svgIcons';
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import { LocaleContext } from '../../../components/LocaleContext';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../../utils/tabulatorScroll';
import { getEffectiveMask } from '../../../utils/effectiveMask';
import { applyUserMaskRowToTabulator, cleanSorters, safeParse, sanitizeTabulatorLayoutAttributes } from '../../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../../utils/tabulatorPersistLayout';
import { getTabulatorLayoutForStandardMask } from '../../../utils/tabulatorLayoutUtils';
import { inputOnEnterHeaderFilter } from '../../../utils/inputOnEnterHeaderFilter';
import { dateUserHeaderFilter } from '../../../utils/dateUserHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, tabulatorDateUserColumnMinWidth, TABULATOR_LIST_ID_MIN_WIDTH } from '../../../utils/tabulatorColumnMinWidth';
import PrintOptionsDialog from '../../../components/PrintOptionsDialog';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../../utils/serviceTaskExport';
import { getTimeUnitDays } from '../../../utils/moduleSettings';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../../utils/exportExcelNaming';
import TabulatorMaskDialog from '../../../utils/tabulatorMaskDialog';
import { applyTabulatorRuntimeI18n, setTabulatorColumnTitle } from '../../../utils/tabulatorRuntimeI18n';

const TASK_TEMPLATE_COLUMN_I18N_KEYS = Object.freeze({
  id: 'ServiceTaskTable.Col.Id',
  name: 'TaskTemplate.Name',
  rows_count: 'TaskTemplate.RowCount',
  created_at: 'TaskTemplate.CreatedAt',
  updated_at: 'TaskTemplate.UpdatedAt',
});

const DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE = Object.freeze({
  id: true,
  name: true,
  rows_count: true,
  total_duration: true,
  created_at: true,
  updated_at: true,
});

function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE };
  return merged;
}

function normalizeTaskTemplateColumnVisibilityForSave(stateObj) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE)) {
    out[field] = stateObj?.[field] !== false;
  }
  return out;
}

/** Štandardná maska: len dátové stĺpce (nie rowSelection / editor). */
function columnsFromTabulatorForStandardMaskSave(raw) {
  const out = {};
  for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE)) {
    out[field] = raw?.[field] !== false;
  }
  return out;
}

function ensureTaskTemplateMaskDialogState(partial) {
  const merged = mergeVisibility(partial);
  return merged;
}

function countTaskTemplateDataColumnsVisible(vis) {
  if (!vis || typeof vis !== 'object') return 0;
  return Object.keys(DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE).filter((k) => vis[k] !== false).length;
}

function clearHeaderFilterForField(table, field) {
  if (!table || field == null) return;
  try {
    table.setHeaderFilterValue?.(field, '', true);
  } catch (_) {}
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE)) {
    if (visibilityMap[f] === false) {
      clearHeaderFilterForField(table, f);
      table.hideColumn?.(f);
    } else {
      table.showColumn?.(f);
    }
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function computeTotalDurationBusinessDays(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return 0;
    const isWeekend = (dt) => dt.weekday === 6 || dt.weekday === 7;
    const addBusinessDays = (start, days) => {
    let d = DateTime.now().startOf('day');
      if (typeof start === 'string') d = DateTime.fromISO(start).startOf('day');
      else if (start instanceof DateTime) d = start.startOf('day');
      let left = Math.max(0, Math.floor(Number(days) || 0));
      while (left > 0) {
        d = d.plus({ days: 1 });
        if (!isWeekend(d)) left--;
      }
      return d;
    };
  let chainAnchor = DateTime.now().startOf('day');
    let blockMax = chainAnchor;
    const sorted = [...rows].sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
    sorted.forEach((row, idx) => {
      if (idx === 0 || row.chain_mode === 'sequential') {
        blockMax = chainAnchor;
      }
      const dur = Math.max(0, Math.floor(Number(row.duration_days) || 0));
      const expected = addBusinessDays(chainAnchor, dur);
      if (expected > blockMax) blockMax = expected;
      const next = sorted[idx + 1];
      const blockEndsHere = !next || next.chain_mode === 'sequential';
      if (blockEndsHere) {
        chainAnchor = blockMax;
      }
    });
    const businessDaysDiff = (a, b) => {
      if (!a || !b) return 0;
    const from = a < b ? a : b;
    const to = a < b ? b : a;
      let count = 0;
      let cur = from;
      while (cur < to) {
        cur = cur.plus({ days: 1 });
        if (!isWeekend(cur)) count++;
      }
      return count;
    };
  return businessDaysDiff(DateTime.now().startOf('day'), chainAnchor);
}

export default function TaskTemplateList({ templateContext = 'service' }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { setDialogText, setVisible, openFavoriteDialog, toast, setFastView, setUserMaskFilter, setStandardMaskFilter, getUserInitials, getUserColor, getDateFormat, formatDate, openChosenNotificationPreferences } = useContext(UtilContext);
  const { t, locale } = useContext(LocaleContext);
  const ref = useRef(null);
  const overlayRef = useRef(null);
  const isRestoringRef = useRef(false);
  const submittedSearchValueRef = useRef('');
  const savedHScrollRef = useRef({ left: 0, pending: false });

  const [editIcon, setEditIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchIconColor, setSearchIconColor] = useState('');
  const [maskVisible, setMaskVisible] = useState(false);
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE });
  const [expandedKeys, setExpandedKeys] = useState({});
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');
  const normalizedContext = templateContext === 'crm' ? 'crm' : 'service';
  const isCrmContext = normalizedContext === 'crm';
  const listPageKey = isCrmContext ? 'CustomerTaskTemplateList' : 'TaskTemplateList';
  const detailPageKey = isCrmContext ? 'CustomerTaskTemplateAdd' : 'TaskTemplateAdd';
  const detailMask = isCrmContext ? 'cust026' : 'intr007';
  const canAddTemplate = user?.permissions?.includes(isCrmContext ? 'add customer_management-setup' : 'add service_task-setup');
  const canEditTemplate = user?.permissions?.includes(isCrmContext ? 'edit customer_management-setup' : 'edit service_task-setup');
  const canDeleteTemplate = user?.permissions?.includes(isCrmContext ? 'delete customer_management-setup' : 'delete service_task-setup');

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);

  const captureHScroll = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    if (!holder) return;
    savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
  }, [getTable]);

  const restoreHScroll = useCallback(() => {
    if (!savedHScrollRef.current.pending) return;
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    const header = table.columnManager?.contentsElement;
    if (!holder) return;
    const left = savedHScrollRef.current.left;
    const apply = () => {
      holder.scrollLeft = left;
      if (header) header.scrollLeft = left;
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(apply, 50);
        setTimeout(() => {
          if (savedHScrollRef.current.pending) {
            apply();
            savedHScrollRef.current.pending = false;
          }
        }, 180);
      });
    });
  }, [getTable]);

  const canSetStandardMask = user?.permissions?.includes('set standard mask');
  const hasApplicableStandardMask = useMemo(() => {
    const row = (state.standard_mask_filters || []).find((m) => m.page === listPageKey);
    if (!row) return false;
    const attributes = sanitizeTabulatorLayoutAttributes(safeParse(row.attributes, []));
    if (!Array.isArray(attributes) || attributes.length <= 1) return false;
    const cols = safeParse(row.columns, {});
    const merged = mergeVisibility(cols && typeof cols === 'object' ? cols : {});
    return Object.values(merged).some(Boolean);
  }, [state.standard_mask_filters, listPageKey]);
  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;
  const unitDays = getTimeUnitDays(state?.module_settings || []);

  const tableData = useMemo(() => {
    const list = (state.task_templates || []).filter((t) => (t.template_context || 'service') === normalizedContext);
    return list.map((t) => ({
      ...t,
      rows_count: Array.isArray(t.rows_json) ? t.rows_json.length : 0,
      total_duration: computeTotalDurationBusinessDays(t.rows_json),
    }));
  }, [state.task_templates, normalizedContext]);

  const parseHeaderFiltersToParams = useCallback(() => {
    const table = getTable();
    const hf = table?.getHeaderFilters?.() ?? [];
    const out = {};
    hf.forEach((f) => {
      if (!f?.field) return;
      const v = f.value;
      if (v == null || String(v).trim() === '') return;
      out[`filter_${f.field}`] = v;
    });
    return out;
  }, [getTable]);

  const getPrintColumnsOrder = useCallback(() => {
    const table = getTable();
    const allowed = Object.keys(DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE);
    const fallback = () => allowed.filter((f) => columnsVisible[f] !== false);
    if (!table?.getColumns) return fallback();
    const allowedSet = new Set(allowed);
    const out = [];
    const seen = new Set();
    try {
      table.getColumns().forEach((col) => {
        const field = col.getField?.() ?? col.getDefinition?.()?.field;
        if (!field || !allowedSet.has(field)) return;
        if (columnsVisible[field] === false) return;
        if (seen.has(field)) return;
        seen.add(field);
        out.push(field);
      });
    } catch (_) {
      return fallback();
    }
    return out.length > 0 ? out : fallback();
  }, [getTable, columnsVisible]);

  const buildTemplateExportQueryString = useCallback((opts = {}) => {
    const table = getTable();
    const params = {
      context: normalizedContext,
      ...parseHeaderFiltersToParams(),
      toolbar_search: String(submittedSearchValueRef.current ?? '').trim() || undefined,
      sorters: JSON.stringify(cleanSorters(table?.getSorters?.() ?? [])),
    };
    return buildExportQueryStringFromParams(params, opts);
  }, [getTable, parseHeaderFiltersToParams, normalizedContext]);

  const runTemplatePrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText(t('Common.PreparingPrint'));
      setVisible(true);
      const suffix = buildTemplateExportQueryString({
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns: getPrintColumnsOrder(),
      });
      const html = await fetchTextGet(`/api/task_templates/export/print${suffix}`);
      if (!openPrintHtmlWindow(html)) {
        toast?.current?.show({ severity: 'warn', summary: t('Common.Print'), detail: t('ServiceTask.EnablePopups'), life: 4000 });
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Print'), detail: e?.message || t('ServiceTask.PrintFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildTemplateExportQueryString, getPrintColumnsOrder, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, toast]);

  const exportTemplateXlsx = useCallback(async () => {
    try {
      setDialogText(t('Common.PreparingExport'));
      setVisible(true);
      const suffix = buildTemplateExportQueryString({
        includePrintColumns: true,
        printColumns: getPrintColumnsOrder(),
      });
      const defaultXlsx = xlsxDownloadFilename(t(EXPORT_DOCUMENT_TITLE_KEYS.TASK_TEMPLATES));
      const { blob, filename } = await downloadBlobGet(`/api/task_templates/export/xlsx${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Export'), detail: e?.message || t('ServiceTask.ExportFailed'), life: 4500 });
    } finally {
      setVisible(false);
    }
  }, [buildTemplateExportQueryString, getPrintColumnsOrder, setDialogText, setVisible, toast]);

  const reloadTemplates = useCallback(() => {
    captureHScroll();
    getRequest(`/api/task_templates?context=${normalizedContext}`).then((r) => {
      const next = [
        ...(state.task_templates || []).filter((tpl) => (tpl.template_context || 'service') !== normalizedContext),
        ...(r.data || []),
      ];
      dispatch({ type: 'load_task_templates', newTaskTemplates: next });
    });
  }, [captureHScroll, dispatch, normalizedContext, state.task_templates]);

  const createNewTemplateFromList = useCallback(async () => {
    setDialogText(t('TaskTemplate.CreatingTemplate'));
    setVisible(true);
    const fd = new FormData();
    fd.append('name', '');
    fd.append('template_context', normalizedContext);
    fd.append('rows_json', JSON.stringify([]));
    try {
      const res = await postRequest('/api/task_templates', fd, true);
      if (!res?.data?.id) {
        setVisible(false);
        let detail = t('TaskTemplate.CreateFailed');
        if (res?.message) detail = typeof res.message === 'string' ? res.message : detail;
        else if (res?.errors && typeof res.errors === 'object') {
          const parts = Object.values(res.errors).flat();
          if (parts.length) detail = parts.join(' ');
        }
        toast?.current?.show({ severity: 'error', summary: t('Chyba'), detail, life: 5000 });
        return;
      }
      captureHScroll();
      const refresh = await getRequest(`/api/task_templates?context=${normalizedContext}`);
      const next = [
        ...(state.task_templates || []).filter((tpl) => (tpl.template_context || 'service') !== normalizedContext),
        ...(refresh.data || []),
      ];
      dispatch({ type: 'load_task_templates', newTaskTemplates: next });
      setVisible(false);
      const full = (refresh.data || []).find((t) => t.id === res.data.id) || res.data;
      dispatch({
        type: 'create_new_tab', name: t('TaskTemplate.EditTemplate'), translationKey: 'TaskTemplate.EditTemplate', legacyLabel: t('TaskTemplate.EditTemplate'),
        page: 'TaskTemplateAdd',
        props: { template: full, type: 'edit', templateContext: normalizedContext },
        mask: detailMask,
      });
    } catch (e) {
      setVisible(false);
      toast?.current?.show({
        severity: 'error',
        summary: t('Chyba'),
        detail: e?.message || t('TaskTemplate.CreateFailed'),
        life: 4000,
      });
    }
  }, [captureHScroll, detailMask, dispatch, normalizedContext, setDialogText, setVisible, state.task_templates, toast]);

  const onSelectRow = useCallback((data) => {
    const count = Array.isArray(data) ? data.length : 0;
    setEditIcon(!canEditTemplate || count !== 1);
    setCopyIcon(!canAddTemplate || count !== 1);
    setDeleteIcon(!canDeleteTemplate || count < 1);
  }, [canAddTemplate, canDeleteTemplate, canEditTemplate]);

  const deleteSelected = useCallback(() => {
    const table = getTable();
    const selected = table?.getSelectedData?.() ?? [];
    Promise.all(selected.map((x) => deleteRequest(`/api/task_templates/${x.id}`)))
      .then(() => {
        toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: t('ServiceTask.RecordDeleted'), life: 3000 });
        reloadTemplates();
      })
      .catch((e) => {
        toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Rejected'), detail: e?.message || t('ServiceTask.RecordDeleteFailed'), life: 3000 });
      });
  }, [getTable, reloadTemplates, toast]);

  const confirmDelete = useCallback(() => {
    confirmDialog({
      message: t('ServiceTask.ConfirmDeleteRecord'),
      header: t('ServiceTask.ConfirmDeleteTitle'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: deleteSelected,
    });
  }, [deleteSelected]);

  const normalizeFilter = (headerValue, rowValue) => {
    if (!headerValue) return true;
    const norm = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return norm(rowValue).includes(norm(headerValue));
  };

  const dateOrUserFilter = (headerValue, rowValue, rowData, filterParams) => {
    if (!headerValue) return true;
    const raw = String(headerValue || '').trim();
    if (raw.startsWith('range:')) {
      const p = raw.slice('range:'.length).split(':');
      if (p.length >= 2 && p[0] && p[1]) {
        const from = DateTime.fromFormat(p[0], 'yyyy-MM-dd').startOf('day');
        const to = DateTime.fromFormat(p[1], 'yyyy-MM-dd').endOf('day');
        const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
        return dt.isValid ? dt >= from && dt <= to : false;
      }
      return true;
    }
    const search = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const userObj = filterParams?.name === 'created_at'
      ? (rowData?.created_by_user ?? rowData?.contractor)
      : (rowData?.updated_by_user ?? rowData?.last_touched_by_user);
    const uname = userObj?.employee?.name || userObj?.username || '';
    const dt = DateTime.fromISO(String(rowData?.[filterParams?.name] || ''));
    const dateStr = dt.isValid ? `${dt.toFormat(dateFmt)} ${dt.toFormat(dateTimeFmt)} ${dt.toFormat('yyyy-MM-dd')}` : '';
    const hay = `${uname} ${dateStr}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return hay.includes(search);
  };

  function RowCountFormatter({ cell }) {
    return cell.getData()?.rows_count ?? 0;
  }

  function TotalDurationFmt({ cell }) {
    const v = cell.getData()?.total_duration;
    return <>{v} {unitDays}</>;
  }

  function CreatedAtCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const user = rowData?.created_by_user ?? rowData?.contractor;
    const text = formatDate
      ? formatDate(rowData?.created_at, dateTimeFmt, true)
      : DateTime.fromISO(String(rowData?.created_at || '')).toFormat(dateTimeFmt);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {user ? (
          <Avatar
            className="shrink-0"
            title={user?.employee?.name || user?.username}
            label={getUserInitials(user?.employee?.name || user?.username)}
            shape="circle"
            style={{ backgroundColor: getUserColor(user?.username), color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  function UpdatedAtCellFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const user = rowData?.updated_by_user ?? rowData?.last_touched_by_user;
    const text = formatDate
      ? formatDate(rowData?.updated_at, dateTimeFmt, true)
      : DateTime.fromISO(String(rowData?.updated_at || '')).toFormat(dateTimeFmt);
    return (
      <div className="flex items-center justify-start w-full h-full min-w-0 gap-1">
        {user ? (
          <Avatar
            className="shrink-0"
            title={user?.employee?.name || user?.username}
            label={getUserInitials(user?.employee?.name || user?.username)}
            shape="circle"
            style={{ backgroundColor: getUserColor(user?.username), color: '#ffffff' }}
          />
        ) : null}
        <div className="min-w-0 flex-1 truncate whitespace-nowrap" title={text || undefined}>{text}</div>
      </div>
    );
  }

  function onEditRow(template) {
    dispatch({ type: 'create_new_tab', name: t('TaskTemplate.EditTemplate'), translationKey: 'TaskTemplate.EditTemplate', legacyLabel: t('TaskTemplate.EditTemplate'), page: 'TaskTemplateAdd', props: { template, type: 'edit', templateContext: normalizedContext }, mask: detailMask });
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => onEditRow(rowData)}><EditRowIcon /></button>
      </div>
    );
  }

  const columns = useMemo(() => ([
    {
      field: 'rowSelection',
      formatter: 'rowSelection',
      titleFormatter: 'rowSelection',
      hozAlign: 'center',
      width: 44,
      headerSort: false,
      resizable: false,
      frozen: true,
      print: false,
      download: false,
    },
    {
      title: '',
      field: 'editor',
      hozAlign: 'center',
      width: 44,
      headerSort: false,
      formatter: reactFormatter(<EditRowFormatter />),
      resizable: false,
      frozen: true,
      print: false,
      download: false,
    },
    {
      title: t('ServiceTaskTable.Col.Id'),
      field: 'id',
      minWidth: TABULATOR_LIST_ID_MIN_WIDTH,
      widthGrow: 0.6,
      hozAlign: 'left',
      sorter: 'number',
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: normalizeFilter,
    },
    { title: t('TaskTemplate.Name'), field: 'name', hozAlign: 'left', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: normalizeFilter, minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Name')), widthGrow: 1 },
    { title: t('TaskTemplate.RowCount'), field: 'rows_count', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, hozAlign: 'left', minWidth: tabulatorColumnMinWidthFromTitle(t('TaskTemplate.RowCount')), widthGrow: 0.8, formatter: reactFormatter(<RowCountFormatter />) },
    { title: t('TaskTemplate.DurationDays', { unit: unitDays }), field: 'total_duration', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, hozAlign: 'left', minWidth: tabulatorColumnMinWidthFromTitle(t('TaskTemplate.DurationDays', { unit: unitDays })), widthGrow: 1, formatter: reactFormatter(<TotalDurationFmt />) },
    { title: t('TaskTemplate.CreatedAt'), field: 'created_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(t('Common.Column.CreatedAt')), widthGrow: 1, formatter: reactFormatter(<CreatedAtCellFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'created_at' }, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt } },
    { title: t('TaskTemplate.UpdatedAt'), field: 'updated_at', hozAlign: 'left', minWidth: tabulatorDateUserColumnMinWidth(t('TaskTemplate.UpdatedAt')), widthGrow: 1, formatter: reactFormatter(<UpdatedAtCellFormatter />), headerFilter: dateUserHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: dateOrUserFilter, headerFilterFuncParams: { name: 'updated_at' }, headerFilterParams: { dateFormat: dateFmt, dateTimeFormat: dateTimeFmt } },
  ]), [dateFmt, dateTimeFmt, unitDays, t]);

  useEffect(() => {
    const table = getTable();
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: TASK_TEMPLATE_COLUMN_I18N_KEYS,
      placeholder: t('Common.NoData'),
      reformatRows: true,
      onRefreshCells: (tbl, tt) => {
        try {
          const col = tbl.getColumn?.('total_duration');
          if (col) setTabulatorColumnTitle(col, tt('TaskTemplate.DurationDays', { unit: unitDays }));
        } catch (_) { /* keep session */ }
      },
    });
  }, [locale, t, getTable, unitDays]);

  const closeMenu = () => {
    OverflowMenuProvider.setVisible(null);
    setExpandedKeys({});
  };

  const openMaskDialog = () => {
    const merged = mergeVisibility(columnsVisible);
    setColumnsVisible(ensureTaskTemplateMaskDialogState(merged));
    setMaskVisible(true);
    closeMenu();
  };

  const saveUserMaskColumns = () => {
    const normalized = normalizeTaskTemplateColumnVisibilityForSave(columnsVisible);
    if (!Object.values(normalized).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Mask'), detail: t('ServiceTask.SelectAtLeastOneColumn'), life: 4000 });
      return;
    }
    const filtersSnapshot = getTable()?.getHeaderFilters?.() ?? [];
    setUserMaskFilter(listPageKey, 'all', normalized, filtersSnapshot, null, null);
    setColumnsVisible(normalized);
    setMaskVisible(false);
  };

  const confirmSaveStandardMask = () => {
    confirmDialog({
      message:
        t('ServiceTask.StandardMaskSaveInfo'),
      header: t('ServiceTask.SaveStandardMaskTitle'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Save'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        const table = getTable();
        const layout = getTabulatorLayoutForStandardMask(table);
        if (!layout) {
          toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Mask'), detail: t('ServiceTask.TableTryAgainSoon'), life: 4000 });
          return;
        }
        const columnsForSave = columnsFromTabulatorForStandardMaskSave(layout.columns);
        if (!Object.values(columnsForSave).some(Boolean)) {
          toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.StandardMask'), detail: t('ServiceTask.AtLeastOneColumnVisible'), life: 4000 });
          return;
        }
        setStandardMaskFilter(listPageKey, columnsForSave, layout.attributes);
      },
    });
  };

  const confirmApplyStandardLayoutToUserMask = () => {
    closeMenu();
    const standard = (state.standard_mask_filters || []).find((m) => m.page === listPageKey);
    if (!standard) {
      toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.StandardMask'), detail: t('ServiceTask.NoStandardMaskForPage'), life: 4000 });
      return;
    }
    const attributes = sanitizeTabulatorLayoutAttributes(safeParse(standard.attributes, []));
    if (!Array.isArray(attributes) || attributes.length <= 1) {
      toast?.current?.show({
        severity: 'warn',
        summary: t('ServiceTask.StandardMask'),
        detail: t('ServiceTask.StandardMaskInvalidLayout'),
        life: 5000,
      });
      return;
    }
    const jsonColumns = safeParse(standard.columns, {});
    const mergedCols = mergeVisibility(jsonColumns && typeof jsonColumns === 'object' ? jsonColumns : {});
    const colsForSave = normalizeTaskTemplateColumnVisibilityForSave(mergedCols);
    if (!Object.values(colsForSave).some(Boolean)) {
      toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.StandardMask'), detail: t('ServiceTask.NoVisibleColumnsInStandardMask'), life: 4000 });
      return;
    }
    confirmDialog({
      message:
        t('ServiceTask.StandardMaskApplyInfo'),
      header: t('ServiceTask.StandardMask'),
      icon: 'pi pi-info-circle',
      acceptLabel: t('Common.Apply'),
      rejectLabel: t('Common.Cancel'),
      accept: () => {
        setUserMaskFilter(listPageKey, 'all', colsForSave, null, attributes, null, () => {
          const table = getTable();
          if (!table) return;
          isRestoringRef.current = true;
          try {
            table.setColumnLayout?.(attributes);
          } catch (_) {}
          setColumnsVisible(colsForSave);
          applyDataColumnVisibilityToTable(table, colsForSave);
          setTimeout(() => {
            isRestoringRef.current = false;
            try {
              table.redraw?.(true);
            } catch (_) {}
          }, 0);
        });
      },
    });
  };

  function setMask(field, value) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE, field)) return;
    const table = getTable();
    const next = { ...DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE, ...columnsVisible, [field]: value };
    if (value === false && countTaskTemplateDataColumnsVisible(next) < 1) {
      toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Mask'), detail: t('ServiceTask.AtLeastOneColumnMustRemainVisible'), life: 3500 });
      return;
    }
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  }

  function maskSelectAll() {
    const next = { ...DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE };
    for (const k of Object.keys(next)) next[k] = true;
    const table = getTable();
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  }

  function maskDeselectAll() {
    const next = {};
    for (const k of Object.keys(DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE)) next[k] = false;
    next.id = true;
    const table = getTable();
    applyDataColumnVisibilityToTable(table, next);
    setColumnsVisible(next);
  }

  const itemRenderer = (item, options) => (
    <button type="button" className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px] border-0 text-left" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </button>
  );

  const items = [{
    key: '0',
    label: t('ServiceTask.Actions'),
    icon: 'pi pi-sliders-v',
    template: itemRenderer,
    items: [
      { key: '0_1', label: t('ServiceTask.SetFilter'), icon: 'pi pi-filter', command: () => { setUserMaskFilter(listPageKey, 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null); captureHScroll(); closeMenu(); }, template: itemRenderer },
      { key: '0_2', label: t('ServiceTask.ClearFilter'), icon: 'pi pi-filter-slash', command: () => { setUserMaskFilter(listPageKey, 'set_filters', null, [], null); getTable()?.clearHeaderFilter?.(); captureHScroll(); closeMenu(); }, template: itemRenderer },
      { key: '0_3', label: t('ServiceTask.SetSort'), icon: 'pi pi-sort-alt', command: () => { setUserMaskFilter(listPageKey, 'set_sorters', null, null, null, cleanSorters(getTable()?.getSorters?.() ?? [])); captureHScroll(); closeMenu(); }, template: itemRenderer },
      { key: '0_4', label: t('ServiceTask.ClearSort'), icon: 'pi pi-sort-alt-slash', command: () => { setUserMaskFilter(listPageKey, 'set_sorters', null, null, null, []); getTable()?.setSort?.([]); captureHScroll(); closeMenu(); }, template: itemRenderer },
      { key: '0_5', label: t('ServiceTask.SetMask'), icon: 'pi pi-server', command: openMaskDialog, template: itemRenderer },
      ...(hasApplicableStandardMask
        ? [{
          key: '0_5s',
          label: t('ServiceTask.StandardMask'),
          icon: 'pi pi-clone',
          command: confirmApplyStandardLayoutToUserMask,
          template: itemRenderer,
        }]
        : []),
      {
        key: '0_6',
        label: t('ServiceTask.Notifications'),
        icon: 'pi pi-envelope',
        command: () => { openChosenNotificationPreferences(listPageKey); closeMenu(); },
        template: itemRenderer,
      },
    ],
  }];

  const handleClickOutside = (event) => {
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      setExpandedKeys({});
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const searchTable = () => {
    captureHScroll();
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    const table = getTable();
    if (!table) return;
    if (normalized === '') {
      table.clearFilter(true);
    } else {
      const norm = (s) => String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const needle = norm(normalized);
      table.setFilter((data) => norm(JSON.stringify(data)).includes(needle));
    }
    setVisibleSearch(false);
  };

  const scrollToFirst = () => scrollToFirstUtil(getTable());
  const scrollUp = () => scrollUpUtil(getTable());
  const scrollDown = () => scrollDownUtil(getTable());
  const scrollToLast = () => scrollToLastUtil(getTable());

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="add"><button type="button" className="disabled:opacity-25" disabled={!canAddTemplate} onClick={createNewTemplateFromList}><AddIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="edit"><button type="button" className="disabled:opacity-25" disabled={editIcon} onClick={() => dispatch({ type: 'create_new_tab', name: t('TaskTemplate.EditTemplate'), translationKey: 'TaskTemplate.EditTemplate', legacyLabel: t('TaskTemplate.EditTemplate'), page: 'TaskTemplateAdd', props: { template: getTable()?.getSelectedData?.()?.[0], type: 'edit', templateContext: normalizedContext }, mask: detailMask })}><EditIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="copy"><button type="button" className="disabled:opacity-25" disabled={copyIcon} onClick={() => dispatch({ type: 'create_new_tab', name: t('TaskTemplate.NewTemplate'), translationKey: 'TaskTemplate.NewTemplate', legacyLabel: t('TaskTemplate.NewTemplate'), page: 'TaskTemplateAdd', props: { template: getTable()?.getSelectedData?.()?.[0], type: 'add', templateContext: normalizedContext }, mask: detailMask })}><CopyIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="delete"><button type="button" className="disabled:opacity-25" disabled={deleteIcon} onClick={confirmDelete}><DeleteIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="print"><button type="button" title={t('Common.Print')} onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button
              type="button"
              title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')}
              onClick={
                searchIconColor !== 'active'
                  ? () => setVisibleSearch(true)
                  : () => {
                      setSearchIconColor('');
                      setSearchValue('');
                      submittedSearchValueRef.current = '';
                      const table = getTable();
                      table?.clearFilter?.(true);
                    }
              }
            >
              {searchIconColor === '' ? (
                <MdSearch fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />
              ) : (
                <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" />
              )}
            </button>
          </div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="export"><button type="button" title={t('Common.ExportToExcel')} onClick={exportTemplateXlsx}><ExportIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh"><button type="button" title={t('Common.Refresh')} onClick={reloadTemplates}><RefreshIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="first"><button type="button" title={t('Icon.First')} onClick={scrollToFirst}><BackToFirstIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="back"><button type="button" title={t('Icon.Previous')} onClick={scrollUp}><BackIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="forward"><button type="button" title={t('Icon.Next')} onClick={scrollDown}><ForwardIcon /></button></div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="last"><button type="button" title={t('Icon.Last')} onClick={scrollToLast}><ForwardToLastIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite"><button type="button" title={t('Common.AddToFavorites')} onClick={() => openFavoriteDialog(t('TaskTemplate.PlannerTemplates'), listPageKey)}><FavoriteIcon /></button></div>
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="price"><button type="button" title={t('Common.QuickView')} onClick={() => setFastView(listPageKey, columnsVisible, columns)}><PriceIcon /></button></div>
          {canSetStandardMask ? (
            <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button
                type="button"
                title={t('ServiceTask.SaveStandardMaskTooltip')}
                aria-label={t('ServiceTask.SaveStandardMaskTitle')}
                onClick={confirmSaveStandardMask}
                className="flex items-center justify-center"
              >
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="vert"><VerticalLineIcon /></div>
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>

      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
          <div className="inline-block w-full py-2">
              <ReactTabulator
                key={`task-template-list-${normalizedContext}`}
                data={tableData}
                className="h-[calc(100vh_-_193px)]"
                onRef={(r) => { ref.current = r?.current; }}
                columns={columns}
                events={{
                  rowSelectionChanged: onSelectRow,
                  tableBuilt: () => {
                    const table = getTable();
                    if (!table) return;
                    const effective = getEffectiveMask(listPageKey, state);
                    if (effective?.type === 'user' && effective.userMask) {
                      const m = effective.userMask;
                      isRestoringRef.current = true;
                      applyUserMaskRowToTabulator(table, m, {
                        setColumnsVisible: (v) => setColumnsVisible(mergeVisibility(v)),
                        clearHeaderFiltersWhenEmpty: true,
                        applyColumnVisibilityFn: (tbl, jc, setVis) => {
                          const merged = mergeVisibility(jc);
                          setVis(merged);
                          applyDataColumnVisibilityToTable(tbl, merged);
                        },
                      });
                      {
                        const merged = mergeVisibility(safeParse(m.columns, {}));
                        for (const field of Object.keys(DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE)) {
                          if (merged[field] === false) clearHeaderFilterForField(table, field);
                        }
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else if (effective?.type === 'standard' && effective.standardMask) {
                      const m = effective.standardMask;
                      isRestoringRef.current = true;
                      const attributes = sanitizeTabulatorLayoutAttributes(safeParse(m.attributes, []));
                      const json_columns = safeParse(m.columns, {});
                        if (Array.isArray(attributes) && attributes.length > 1) {
                        table.setColumnLayout?.(attributes);
                      }
                      if (json_columns && typeof json_columns === 'object') {
                        const merged = mergeVisibility(json_columns);
                        setColumnsVisible(merged);
                        applyDataColumnVisibilityToTable(table, merged);
                      }
                      setTimeout(() => { isRestoringRef.current = false; }, 0);
                    }
                  },
                  ...getTabulatorPersistLayoutEvents(listPageKey, ref, setUserMaskFilter),
                  headerFilterChanged: () => { if (!isRestoringRef.current) captureHScroll(); },
                  sortChanged: () => { if (!isRestoringRef.current) captureHScroll(); },
                  renderComplete: () => restoreHScroll(),
                }}
                options={{ layout: 'fitColumns', renderHorizontal: 'virtual', movableColumns: true, autoResize: true, headerSortTristate: true }}
              />
            </div>
          </div>
        </div>
      </div>

      <PrintOptionsDialog
        visible={printChoiceVisible}
        onHide={() => setPrintChoiceVisible(false)}
        orientation={printOrientationChoice}
        onOrientationChange={setPrintOrientationChoice}
        layout={printLayoutChoice}
        onLayoutChange={setPrintLayoutChoice}
        onConfirm={runTemplatePrint}
      />

      <Dialog header={t('ServiceTask.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('ServiceTask.GlobalSearchInDisplayedData')}</p>
          <div className="flex items-center gap-3 w-full">
            <input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              type="text"
              className="flex-1 h-10 px-4 border rounded-md focus:border-gray-400"
            />
            <button type="button" onClick={searchTable} className="h-10 w-16 bg-zinc-900 text-white rounded-md hover:bg-zinc-800 flex items-center justify-center">
              <SearchIcon />
            </button>
          </div>
        </div>
      </Dialog>

      <TabulatorMaskDialog
        visible={maskVisible}
        onHide={() => setMaskVisible(false)}
        title={t('Common.SetMask')}
        fields={Object.keys(DEFAULT_COLUMNS_VISIBLE_TASK_TEMPLATE)}
        checkedMap={columnsVisible}
        onToggleField={setMask}
        onSelectAll={maskSelectAll}
        onDeselectAll={maskDeselectAll}
        onSave={saveUserMaskColumns}
        selectAllLabel={t('ServiceTask.SelectAll')}
        deselectAllLabel={t('ServiceTask.DeselectAllExceptId')}
        saveLabel={t('Common.Save').toUpperCase()}
        getFieldLabel={(field) => {
          const col = columns.find((item) => item?.field === field);
          return col?.title || field;
        }}
        inputIdPrefix="tt-mask"
      />
    </>
  );
}
